/**
 * CustomerSelectModal — modal de associação de cliente ao aprovar/confiar um
 * e-mail retido (decisão do dono 2026-07-24). Aberto SÓ quando o contato
 * remetente ainda não tem customer. Associar é OPCIONAL: pode confirmar sem
 * cliente. Quando associado, o `customerId` vai no body do approve e o backend
 * seta `contact.customerId` + o ticket criado herda o cliente.
 *
 * Migrado para `Modal`/`ModalHeader`/`ModalContent`/`ModalFooter` shared
 * (F71/F72/F74): tema claro fixo + bottom-sheet mobile de graça, título 18px
 * como prop tipada e "Cancelar" = `<Button variant="secondary">`. Usa
 * `useCustomersDropdown` (rota read-only `/customers/dropdown`, sem permissão
 * de `customers`) gated ao `isOpen`.
 */

import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui';
import { Select } from '@/shared/components/ui/Select';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';

interface CustomerSelectModalProps {
  isOpen: boolean;
  /** Rótulo da ação em andamento — "Aprovar" | "Confiar" (título do modal). */
  actionLabel: string;
  contactName?: string;
  isSubmitting?: boolean;
  onConfirm: (customerId: string | null) => void;
  onClose: () => void;
}

export const CustomerSelectModal: React.FC<CustomerSelectModalProps> = ({
  isOpen,
  actionLabel,
  contactName,
  isSubmitting = false,
  onConfirm,
  onClose,
}) => {
  const [customerId, setCustomerId] = useState('');
  const { data: customers } = useCustomersDropdown({ enabled: isOpen });

  // Reset ao abrir — não vazar seleção de uma decisão anterior.
  useEffect(() => {
    if (isOpen) setCustomerId('');
  }, [isOpen]);

  if (!isOpen) return null;

  const options = [
    { value: '', label: '— Sem cliente —' },
    ...(customers ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      {/* F74: título 18px como prop tipada do ModalHeader */}
      <ModalHeader title={actionLabel} onClose={onClose} />

      <ModalContent>
        <div data-testid="retention-customer-modal">
          {contactName && (
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
              {contactName}
            </p>
          )}
          <p className="text-sm mb-3" style={{ color: 'var(--theme-muted-text)' }}>
            Associe um cliente a este contato (opcional). O ticket criado virá com o cliente vinculado.
          </p>
          <Select
            label="Cliente"
            value={customerId}
            onChange={(value) => setCustomerId(value)}
            options={options}
            placeholder="— Sem cliente —"
            data-testid="retention-customer-select"
          />
        </div>
      </ModalContent>

      <ModalFooter>
        {/* F71: Cancelar = Button variant="secondary" (bordado) */}
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          data-testid="retention-customer-cancel"
        >
          Cancelar
        </Button>
        <Button
          icon="check"
          loading={isSubmitting}
          onClick={() => onConfirm(customerId || null)}
          data-testid="retention-customer-confirm"
        >
          Confirmar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomerSelectModal;
